/**
 * ALOHA: Post Add-to-Cart Drawer
 * Custom implementation by Aloha
 * Opens exclusively after add-to-cart actions
 * Uses metafield-based recommendations with Shopify API fallback
 */

if (!customElements.get('post-add-drawer')) {
  customElements.define(
    'post-add-drawer',
    class PostAddDrawer extends DrawerElement {
      constructor() {
        super();

        this.onProductAddedListener = this.onProductAdded.bind(this);
        this.lastAddedProduct = null;
        this.addedProductData = null;
      }

      get sectionId() {
        return this.getAttribute('data-section-id');
      }

      get shouldAppendToBody() {
        return false;
      }

      get productContainer() {
        return this.querySelector('#PostAddProduct');
      }

      get recommendationsContainer() {
        return this.querySelector('#PostAddRecommendations');
      }

      get recommendationsMetafield() {
        return this.getAttribute('data-recommendations-metafield');
      }

      connectedCallback() {
        super.connectedCallback();

        // Luister naar add-to-cart events
        document.addEventListener('ajaxProduct:added', this.onProductAddedListener);
      }

      disconnectedCallback() {
        super.disconnectedCallback();
        document.removeEventListener('ajaxProduct:added', this.onProductAddedListener);
      }

      /**
       * Handler voor wanneer een product wordt toegevoegd
       */
      async onProductAdded(event) {
        // Check of er geen andere drawer open is
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer?.hasAttribute('active')) {
          return;
        }

        if (this.hasAttribute('active')) {
          return;
        }

        try {
          const addedProduct = event.detail?.product;
          if (!addedProduct) {
            console.error('No product data in ajaxProduct:added event');
            return;
          }

          // Check of het advanced upsell popup dit product moet afhandelen
          if (this.shouldUseAdvancedUpsell(addedProduct)) {
            return;
          }

          this.lastAddedProduct = addedProduct;

          // Check if this is a bundle (multiple items) or single product
          const isBundleAdd = addedProduct.items && Array.isArray(addedProduct.items);

          // Toon de drawer eerst (voor snellere UX)
          this.show();

          // Render het toegevoegde product (of producten bij bundle)
          if (isBundleAdd) {
            await this.renderAddedProducts(addedProduct.items);
          } else {
            await this.renderAddedProduct(addedProduct);
          }

          // Laad product recommendations - bij meerdere producten, sorteer op prijs
          if (this.recommendationsContainer) {
            if (isBundleAdd) {
              await this.loadRecommendationsForMultipleProducts(addedProduct.items);
            } else {
              await this.loadRecommendations(addedProduct);
            }
          }

          // Update free shipping bar (real-time) - na rendering zodat cart data beschikbaar is
          await this.updateFreeShippingBar();
        } catch (error) {
          console.error('Error showing post-add drawer:', error);
        }
      }

      /**
       * Update free shipping bar real-time
       * Liquid (snippet free-shipping-bar) rekent de balk uit, inclusief landcheck en drempel uit de theme settings.
       */
      async updateFreeShippingBar() {
        const freeShippingBarContainer = this.querySelector('.post-add__free-shipping');
        if (!freeShippingBarContainer) return;

        try {
          const response = await fetch(`${theme.routes.root_url}?section_id=${this.sectionId}`, {
            cache: 'no-cache'
          });
          const parsedHTML = new DOMParser().parseFromString(await response.text(), 'text/html');
          const updatedContainer = parsedHTML.querySelector('.post-add__free-shipping');
          if (!updatedContainer) return;

          freeShippingBarContainer.innerHTML = updatedContainer.innerHTML;

          // Trigger cart update event for other components
          document.dispatchEvent(new CustomEvent('cart:refresh', {
            bubbles: true,
            detail: { open: false }
          }));
        } catch (error) {
          console.error('Error updating free shipping bar:', error);
        }
      }

      /**
       * Render prijs met compare_at_price en adviesprijs support
       * Gebaseerd op product-price.liquid snippet logica
       */
      renderPrice(variant, product) {
        const price = variant.price;
        let compareAtPrice = variant.compare_at_price;
        let isAdviesprijs = false;

        // Check for adviesprijs metafield if no compare_at_price
        if (!compareAtPrice || compareAtPrice === 0) {
          // Try to get adviesprijs from product metafields
          if (product.metafields && product.metafields.custom && product.metafields.custom.adviesprijs) {
            const adviesprijs = product.metafields.custom.adviesprijs;
            if (adviesprijs && adviesprijs > 0) {
              compareAtPrice = adviesprijs;
              isAdviesprijs = true;
            }
          }
        }

        const onSale = compareAtPrice && compareAtPrice > price;

        let priceHtml = `
          <div class="price${onSale ? ' price--on-sale' : ''}">
            <span class="price__regular whitespace-nowrap">
              ${this.formatMoney(price)}
            </span>
        `;

        if (onSale) {
          const label = isAdviesprijs ? 'Adviesprijs: ' : 'Meest getoonde prijs: ';
          const tooltipText = isAdviesprijs
            ? 'De adviesprijs is de aanbevolen prijs voor een product, bepaald door de fabrikant.'
            : 'De laagste prijs in onze shop in de afgelopen 30 dagen. Uitgezonderd speciale promoties.';

          priceHtml += `
            <span class="price__sale-wrapper inline-flex items-center h-auto relative">
              <span class="price__sale-label">${label}</span>
              <span class="price__sale">
                ${this.formatMoney(compareAtPrice)}
              </span>
              <span class="tooltip-icon" data-tooltip-target="price-tooltip-${variant.id}">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="16" x2="12" y2="12"></line>
                  <line x1="12" y1="8" x2="12.01" y2="8"></line>
                </svg>
              </span>
              <span class="tooltip-text" id="price-tooltip-${variant.id}">
                ${tooltipText}
              </span>
            </span>
          `;
        }

        priceHtml += `</div>`;
        return priceHtml;
      }

      /**
       * Render het toegevoegde product
       */
      async renderAddedProduct(productData) {
        if (!this.productContainer) return;

        try {
          const variantId = productData.variant_id || productData.id;

          // Fetch product details
          const response = await fetch(`${window.Shopify.routes.root}products/${productData.handle}.js`);
          const product = await response.json();

          const variant = product.variants.find(v => v.id === variantId) || product.variants[0];
          const image = variant.featured_image || product.featured_image || product.images[0];
          const imageUrl = this.getImageUrl(image);

          // Store product data for recommendations
          this.addedProductData = {
            product: product,
            variant: variant
          };

          // Build product HTML - compact design
          const productHtml = `
            <div class="post-add__product-media">
              ${imageUrl ? `<img src="${imageUrl}" alt="${product.title}" loading="eager">` : ''}
            </div>
            <div class="post-add__product-details">
              <div class="post-add__product-title">${product.title}</div>
              ${variant.title !== 'Default Title' ? `<div class="post-add__product-variant">${variant.title}</div>` : ''}
              <div class="post-add__product-price">
                ${this.renderPrice(variant, product)}
              </div>
            </div>
          `;

          this.productContainer.innerHTML = productHtml;
        } catch (error) {
          console.error('Error rendering added product:', error);
          this.productContainer.innerHTML = '<p>Product toegevoegd aan winkelwagen</p>';
        }
      }

      /**
       * Render meerdere toegevoegde producten (voor product bundles)
       */
      async renderAddedProducts(items) {
        if (!this.productContainer) return;

        try {
          // Fetch all product details in parallel
          const productPromises = items.map(async (item) => {
            try {
              const response = await fetch(`${window.Shopify.routes.root}products/${item.handle}.js`);
              const product = await response.json();
              const variant = product.variants.find(v => v.id === item.variant_id || v.id === item.id) || product.variants[0];
              const image = variant.featured_image || product.featured_image || product.images[0];

              return {
                product,
                variant,
                image,
                quantity: item.quantity || 1
              };
            } catch (error) {
              console.error('Error fetching product:', error);
              return null;
            }
          });

          const productsData = (await Promise.all(productPromises)).filter(p => p !== null);

          if (productsData.length === 0) {
            this.productContainer.innerHTML = '<p>Producten toegevoegd aan winkelwagen</p>';
            return;
          }

          // Store first product for recommendations
          this.addedProductData = {
            product: productsData[0].product,
            variant: productsData[0].variant
          };

          // Build HTML for all products
          const productsHtml = productsData.map(({ product, variant, image, quantity }) => {
            const imageUrl = this.getImageUrl(image);

            return `
              <div class="post-add__bundle-item flex items-center gap-4 pb-3 border-b border-border last:border-b-0 last:pb-0">
                <div class="post-add__product-media shrink-0">
                  ${imageUrl ? `<img src="${imageUrl}" alt="${product.title}" loading="eager">` : ''}
                </div>
                <div class="post-add__product-details grow">
                  <div class="post-add__product-title">${product.title}</div>
                  ${variant.title !== 'Default Title' ? `<div class="post-add__product-variant text-sm opacity-70">${variant.title}</div>` : ''}
                  <div class="post-add__product-price">
                    ${this.renderPrice(variant, product)}
                  </div>
                </div>
                ${quantity > 1 ? `<div class="post-add__product-quantity shrink-0 text-sm opacity-70">×${quantity}</div>` : ''}
              </div>
            `;
          }).join('');

          // Add a wrapper and heading for multiple products
          this.productContainer.innerHTML = `
            <div class="post-add__bundle-products flex flex-col gap-3">
              <div class="post-add__bundle-heading text-sm font-medium opacity-70">
                ${productsData.length} ${productsData.length === 1 ? 'product' : 'producten'} toegevoegd
              </div>
              ${productsHtml}
            </div>
          `;
        } catch (error) {
          console.error('Error rendering added products:', error);
          this.productContainer.innerHTML = '<p>Producten toegevoegd aan winkelwagen</p>';
        }
      }

      /**
       * Laad recommendations voor meerdere producten, gesorteerd op prijs
       * Toont eerst upsells van duurste product, dan volgende in prijs volgorde
       */
      async loadRecommendationsForMultipleProducts(items) {
        if (!this.recommendationsContainer) return;

        try {
          this.recommendationsContainer.classList.add('loading');
          this.recommendationsContainer.innerHTML = '';

          // Fetch alle product details in parallel
          const productPromises = items.map(async (item) => {
            try {
              const response = await fetch(`${window.Shopify.routes.root}products/${item.handle}.js`);
              const product = await response.json();
              const variant = product.variants.find(v => v.id === item.variant_id || v.id === item.id) || product.variants[0];

              return {
                product,
                variant,
                price: variant.price
              };
            } catch (error) {
              console.error('Error fetching product for recommendations:', error);
              return null;
            }
          });

          let productsData = (await Promise.all(productPromises)).filter(p => p !== null);

          // Sorteer producten op prijs (hoog naar laag)
          productsData.sort((a, b) => b.price - a.price);

          let allRecommendations = [];
          let seenProductIds = new Set();
          let recommendationSource = 'shopify';

          // Probeer metafield recommendations voor elk product in prijs volgorde
          if (this.recommendationsMetafield) {
            for (const productData of productsData) {
              // Stel addedProductData in voor huidige product
              this.addedProductData = {
                product: productData.product,
                variant: productData.variant
              };

              const metafieldRecs = await this.loadMetafieldRecommendations();

              if (metafieldRecs.length > 0) {
                recommendationSource = 'metafield';

                // Voeg alleen toe als we dit product nog niet hebben gezien
                for (const rec of metafieldRecs) {
                  if (!seenProductIds.has(rec.id)) {
                    allRecommendations.push(rec);
                    seenProductIds.add(rec.id);
                  }
                }
              }
            }
          }

          // Fallback naar Shopify Recommendations API als geen metafield recommendations
          if (allRecommendations.length === 0) {
            // Gebruik het duurste product voor Shopify API
            const mostExpensiveProduct = productsData[0];
            const productForShopifyAPI = {
              product_id: mostExpensiveProduct.product.id,
              id: mostExpensiveProduct.product.id,
              handle: mostExpensiveProduct.product.handle
            };

            allRecommendations = await this.loadShopifyRecommendations(productForShopifyAPI);
            recommendationSource = 'shopify';
          }

          if (allRecommendations.length === 0) {
            this.recommendationsContainer.innerHTML = '';
            return;
          }

          // Render recommendations
          await this.renderRecommendations(allRecommendations, recommendationSource);
        } catch (error) {
          console.error('Error loading recommendations for multiple products:', error);
          this.recommendationsContainer.innerHTML = '';
        } finally {
          this.recommendationsContainer.classList.remove('loading');
        }
      }

      /**
       * Laad product recommendations
       * Eerst proberen van metafield, dan fallback naar Shopify API
       */
      async loadRecommendations(productData) {
        if (!this.recommendationsContainer) return;

        try {
          this.recommendationsContainer.classList.add('loading');
          this.recommendationsContainer.innerHTML = '';

          let recommendations = [];
          let recommendationSource = 'shopify'; // 'metafield' of 'shopify'

          // Probeer eerst metafield-based recommendations
          if (this.recommendationsMetafield && this.addedProductData) {
            recommendations = await this.loadMetafieldRecommendations();
            if (recommendations.length > 0) {
              recommendationSource = 'metafield';
            }
          }

          // Fallback naar Shopify Recommendations API
          if (recommendations.length === 0) {
            recommendations = await this.loadShopifyRecommendations(productData);
            recommendationSource = 'shopify';
          }

          if (recommendations.length === 0) {
            this.recommendationsContainer.innerHTML = '';
            return;
          }

          // Render recommendations using horizontal product cards
          await this.renderRecommendations(recommendations, recommendationSource);
        } catch (error) {
          console.error('Error loading recommendations:', error);
          this.recommendationsContainer.innerHTML = '';
        } finally {
          this.recommendationsContainer.classList.remove('loading');
        }
      }

      /**
       * Laad recommendations van product metafield
       * Zoekt eerst in huidige pagina, anders via AJAX
       */
      async loadMetafieldRecommendations() {
        if (!this.addedProductData || !this.recommendationsMetafield) {
          return [];
        }

        try {
          const product = this.addedProductData.product;
          const [namespace, key] = this.recommendationsMetafield.split('.');

          if (!namespace || !key) {
            return [];
          }

          let recommendationHandles = [];

          // Check of metafield data beschikbaar is in huidige pagina
          const productMetafieldScript = document.querySelector(`script[data-product-metafields][data-product-handle="${product.handle}"]`);
          if (productMetafieldScript) {
            try {
              const metafieldData = JSON.parse(productMetafieldScript.textContent);
              if (metafieldData.metafields && metafieldData.metafields[namespace] && metafieldData.metafields[namespace][key]) {
                recommendationHandles = metafieldData.metafields[namespace][key] || [];
              }
            } catch (e) {
              // Silently fail
            }
          }

          if (recommendationHandles.length === 0) {
            return [];
          }

          // Fetch recommended products
          const products = [];
          for (const handle of recommendationHandles) {
            try {
              const res = await fetch(`${window.Shopify.routes.root}products/${handle}.js`);
              if (res.ok) {
                const prod = await res.json();
                // Check if product has at least one available variant
                const hasAvailableVariant = prod.variants && prod.variants.some(v => v.available);
                if (prod.available && hasAvailableVariant) {
                  products.push(prod);
                }
              }
            } catch (err) {
              // Silently fail
            }
          }

          return products;
        } catch (error) {
          return [];
        }
      }

      /**
       * Laad recommendations via Shopify Recommendations API
       */
      async loadShopifyRecommendations(productData) {
        try {
          const productId = productData.product_id || productData.id;
          const limit = this.getAttribute('data-limit') || 3;

          const response = await fetch(`${window.Shopify.routes.root}recommendations/products.json?product_id=${productId}&limit=${limit}`);

          if (!response.ok) {
            throw new Error('Failed to fetch recommendations');
          }

          const data = await response.json();
          let products = data.products || [];

          // Filter out unavailable products - check if product has at least one available variant
          products = products.filter(p => {
            const hasAvailableVariant = p.variants && p.variants.some(v => v.available);
            return p.available && hasAvailableVariant;
          });

          return products;
        } catch (error) {
          console.error('Error loading Shopify recommendations:', error);
          return [];
        }
      }

      /**
       * Render recommendations using horizontal product card structure
       */
      async renderRecommendations(products, source = 'shopify') {
        const limit = parseInt(this.getAttribute('data-limit')) || 2;
        const productsToShow = products.slice(0, limit);

        const cardsHtml = productsToShow.map((product, index) => {
          return this.buildHorizontalProductCard(product, index === 0);
        }).join('');

        this.recommendationsContainer.innerHTML = cardsHtml;

        // Update heading based on source
        const headingElement = document.getElementById('PostAddRecommendationsHeading');
        if (headingElement) {
          const metafieldHeading = this.getAttribute('data-metafield-recommendations-heading');
          const shopifyHeading = this.getAttribute('data-recommendations-heading');

          if (source === 'metafield' && metafieldHeading) {
            headingElement.textContent = metafieldHeading;
          } else if (shopifyHeading) {
            headingElement.textContent = shopifyHeading;
          }
        }

        // Setup add-to-cart event listeners
        this.setupProductCardListeners();
      }

      /**
       * Extract image URL from image data (handles both string and object formats)
       */
      getImageUrl(image) {
        if (!image) return '';
        // Handle string format (direct URL)
        if (typeof image === 'string') {
          return image.startsWith('//') ? 'https:' + image : image;
        }
        // Handle object format with .src property
        if (image.src) {
          return image.src.startsWith('//') ? 'https:' + image.src : image.src;
        }
        return '';
      }

      /**
       * Get placeholder SVG for products without images
       */
      getPlaceholderSvg() {
        return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 525 525" class="placeholder" style="color: rgba(var(--color-foreground), 0.15); background: rgba(var(--color-foreground), 0.04);"><path d="M324.87 279.77a81.43 81.43 0 1 0-81.42 81.42 81.42 81.42 0 0 0 81.42-81.42zm-81.42 40.36a40.36 40.36 0 1 1 40.36-40.36 40.36 40.36 0 0 1-40.36 40.36z" fill="currentColor"/><path d="M420 393.42L361 272.47a11.25 11.25 0 0 0-9.49-7.3c-5.59-.41-10.37 3.55-11.86 8.86l-39.47 138.76a11.23 11.23 0 0 0 10.87 14.25h83.25c6.66 0 11.75-5.79 10.87-13.92a11.25 11.25 0 0 0-10.87-9.64h-57.45l30.49-107.17 46.14 96.62z" fill="currentColor"/><path d="M210.32 393.42L169 272.47a11.25 11.25 0 0 0-9.49-7.3c-5.59-.41-10.37 3.55-11.86 8.86l-39.47 138.76a11.23 11.23 0 0 0 10.87 14.25h83.25c6.66 0 11.75-5.79 10.87-13.92a11.25 11.25 0 0 0-10.87-9.64h-57.45l30.49-107.17 46.14 96.62z" fill="currentColor"/></svg>`;
      }

      /**
       * Build horizontal product card HTML (matching cart-drawer style)
       */
      buildHorizontalProductCard(product, accessibility = true) {
        // Get first available variant, fallback to first variant
        const variant = product.variants.find(v => v.available) || product.variants[0];
        const image = variant.featured_image || product.featured_image || product.images[0];
        const imageUrl = this.getImageUrl(image);

        const hasMultipleVariants = product.variants.length > 1;
        const isAvailable = product.available && variant.available;

        const tabindex = accessibility ? '' : ' tabindex="-1"';

        return `
          <div class="horizontal-product flex items-center gap-4 md:gap-6 w-full" data-product-id="${product.id}">
            <a class="horizontal-product__media media media--square relative overflow-hidden shrink-0" href="${product.url}"${tabindex}>
              ${imageUrl ? `<img src="${imageUrl}" alt="${product.title}" loading="lazy" width="180">` : ''}
            </a>
            <div class="horizontal-product__details grow flex flex-col justify-start gap-1d5">
              <div class="flex flex-col gap-1">
                <div class="block">
                  <a href="${product.url}" class="horizontal-product__title reversed-link font-medium text-base leading-tight"${tabindex}>${product.title}</a>
                </div>
              </div>
              <div class="flex flex-col gap-1">
                ${this.renderPrice(variant, product)}
              </div>
            </div>
            <div class="horizontal-product__quantity shrink-0 text-sm sm:block">
              <div class="grid gap-3">
                ${isAvailable ? (
                  hasMultipleVariants ?
                    `<a href="${product.url}" class="post-add__quick-add button button--primary button--small icon-with-text" is="hover-link" aria-label="Bekijk product"${tabindex}>
                      <span class="btn-fill" data-fill></span>
                      <span class="btn-text">
                        <span class="md:block">Bekijk product</span>
                      </span>
                    </a>`
                    :
                    `<button type="button" class="post-add__quick-add button button--primary button--small icon-with-text" is="hover-button" data-variant-id="${variant.id}" data-product-handle="${product.handle}" aria-label="Voeg toe"${tabindex}>
                      <span class="btn-fill" data-fill></span>
                      <span class="btn-text">
                        <span class="md:block" data-sold-out="Uitverkocht">Voeg toe</span>
                      </span>
                    </button>`
                ) :
                  `<span class="button button--primary button--small" disabled="disabled">Uitverkocht</span>`
                }
              </div>
            </div>
          </div>
        `;
      }

      /**
       * Setup event listeners for product card add-to-cart buttons
       */
      setupProductCardListeners() {
        const addButtons = this.recommendationsContainer.querySelectorAll('.post-add__quick-add');

        addButtons.forEach(button => {
          button.addEventListener('click', async (e) => {
            // Only prevent default for buttons, not links
            const variantId = button.getAttribute('data-variant-id');
            const productHandle = button.getAttribute('data-product-handle');

            // If this is a link (no variant-id), allow default behavior
            if (!variantId) return;

            // For buttons with variant-id, prevent default and handle add to cart
            e.preventDefault();
            e.stopPropagation();

            if (button.disabled) return;

            try {
              button.disabled = true;
              const originalText = button.querySelector('.btn-text span').textContent;
              button.querySelector('.btn-text span').textContent = 'Bezig...';

              // Add to cart using same approach as theme.js
              const body = JSON.stringify({
                id: variantId,
                quantity: 1,
                // ALOHA: tag post-add-to-cart drawer upsell selections for upsell reporting
                properties: { _upsell_source: 'post_atc_drawer' },
                sections: this.getSectionsToRender().map((section) => section.section),
                sections_url: window.location.pathname
              });

              const config = {
                ...theme.utils.fetchConfig('javascript'),
                ...{ body }
              };

              const response = await fetch(theme.routes.cart_add_url, config);

              if (!response.ok) {
                const error = await response.json();
                throw new Error(error.description || 'Failed to add product');
              }

              const result = await response.json();

              // Update cart UI
              document.dispatchEvent(new CustomEvent('cart:refresh', {
                bubbles: true,
                detail: { open: false }
              }));

              // Show success feedback
              button.querySelector('.btn-text span').textContent = '✓ Toegevoegd';
              button.style.background = '#171717';
              button.style.color = '#ffffff';
              button.style.borderColor = '#171717';

              // Update free shipping bar after a short delay to ensure cart is updated
              setTimeout(async () => {
                await this.updateFreeShippingBar();
              }, 100);

              // Reset after 2 seconds
              setTimeout(() => {
                button.querySelector('.btn-text span').textContent = originalText;
                button.style.background = '';
                button.style.color = '';
                button.style.borderColor = '';
                button.disabled = false;
              }, 2000);

            } catch (error) {
              console.error('Error adding recommendation to cart:', error);
              button.querySelector('.btn-text span').textContent = 'Fout';
              button.disabled = false;

              setTimeout(() => {
                button.querySelector('.btn-text span').textContent = 'Voeg toe';
              }, 2000);
            }
          });
        });
      }

      /**
       * Get sections to render for cart update
       */
      getSectionsToRender() {
        return [
          {
            section: 'cart-drawer'
          }
        ];
      }

      /**
       * Format money volgens Shopify's money format
       */
      formatMoney(cents) {
        if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
          return Shopify.formatMoney(cents, theme.moneyFormat || '{{amount}}');
        }
        // Fallback
        const amount = (cents / 100).toFixed(2);
        return `€${amount}`;
      }

      /**
       * Check of het advanced upsell page dit product moet afhandelen
       * Als ja, redirect naar de upsell pagina
       */
      shouldUseAdvancedUpsell(productData) {
        const isBundleAdd = productData.items && Array.isArray(productData.items);

        let handle, allItems;
        if (isBundleAdd) {
          // Sort items by price descending; use most expensive as the "main" product
          const sorted = [...productData.items].sort((a, b) => b.price - a.price);
          handle = sorted[0]?.handle;
          allItems = productData.items;
        } else {
          handle = productData.handle;
          allItems = null;
        }

        if (!handle) return false;

        const metafieldScript = document.querySelector(
          `script[data-product-metafields][data-product-handle="${handle}"]`
        );
        if (!metafieldScript) return false;

        try {
          const data = JSON.parse(metafieldScript.textContent);
          if (data.metafields?.custom?.advanced_upsell_section === true) {
            // Redirect to the upsell page with the product handle
            // Use Shopify.routes.root to preserve language prefix (e.g., /en/)
            const params = new URLSearchParams();
            params.set('product', handle);
            if (allItems) {
              // Encode all items as "handle:variantId" pairs (comma-separated)
              params.set('products', allItems.map(i => `${i.handle}:${i.variant_id || i.id}`).join(','));
            }
            window.location.href = `${window.Shopify.routes.root}pages/extras?${params.toString()}`;
            return true;
          }
          return false;
        } catch (e) {
          return false;
        }
      }

      /**
       * Toon de drawer
       */
      show(focusElement = null, animate = true) {
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer?.hasAttribute('active')) {
          console.log('Cart drawer is active, skipping post-add drawer');
          return;
        }

        if (cartDrawer?.hasAttribute('open')) {
          cartDrawer.hide();
        }

        super.show(focusElement, animate);
      }

      /**
       * Verberg de drawer
       */
      hide(animate = true) {
        super.hide(animate);

        // Reset checkmark animation
        const checkmark = this.querySelector('.checkmark');
        if (checkmark) {
          checkmark.style.animation = 'none';
          requestAnimationFrame(() => {
            checkmark.style.animation = '';
          });
        }
      }
    }
  );
}

/**
 * Conflict preventie utility
 */
class DrawerConflictManager {
  constructor() {
    this.activeDrawer = null;
    this.init();
  }

  init() {
    document.addEventListener('drawer:open', (event) => {
      const drawer = event.detail.drawer;

      if (this.activeDrawer && this.activeDrawer !== drawer) {
        this.activeDrawer.hide(false);
      }

      this.activeDrawer = drawer;
    });

    document.addEventListener('drawer:close', (event) => {
      const drawer = event.detail.drawer;

      if (this.activeDrawer === drawer) {
        this.activeDrawer = null;
      }
    });
  }
}

// Initialiseer conflict manager
if (typeof window.drawerConflictManager === 'undefined') {
  window.drawerConflictManager = new DrawerConflictManager();
}
